import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getShipTosDataView } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import updateSearchFields from "@insite/client-framework/Store/Pages/Addresses/Handlers/UpdateSearchFields";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Pagination, { PaginationPresentationProps } from "@insite/mobius/Pagination";
import Typography from "@insite/mobius/Typography";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import translate from "@insite/client-framework/Translate";
import { StyledTypography } from "@insite/mobius/Link";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { css } from "styled-components";
import themeColors, { themeFontSizes, themeFontWeights, themeIndentSizes } from "@MasterBluePrint/Styles/variables";

const mapStateToProps = (state: ApplicationState) => ({
    shipTosDataView: getShipTosDataView(state, state.pages.addresses.getShipTosParameter),
});

const mapDispatchToProps = {
    updateSearchFields,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface AddressBookPaginationStyles {
    pagination?: PaginationPresentationProps;
    wrapper?: InjectableCss;
    totalShippingAddresses?: InjectableCss;
}

export const addressBookPaginationStyles: AddressBookPaginationStyles = {
    wrapper: {
        css: css`
        position: relative;
        display: flex;
        align-items: center;
        padding: 0.75rem 0;
        font-size: 0.875rem;
        margin-bottom: 0.9375rem;
        margin-top: 0.9375rem;
        border-top: 1px solid #E4E8F0;
        border-bottom: 1px solid #E4E8F0;

        .address-pagination {
            position: absolute;
            right: 0;

            > span label {
                color: ${themeColors.colorMidGrey};
                letter-spacing: 0;
                font-family: 'Roboto',sans-serif;
                font-size: 0.875rem;
                font-weight: ${themeFontWeights.fontWeightMedium};
                letter-spacing: 0;
                padding: 0.625rem 0.9375rem 0 0;
            }

            nav {
                ul.pagination-arrow li:nth-child(1) {
                    margin-left: 1.5625rem;
                    margin-right: 1.75rem;
                }
                ul.pagination-arrow li:nth-child(3) {
                    margin-left: 1.8125rem;
                }
                ul.pagination-arrow li:nth-last-child(1) {
                    margin-left: 1.75rem;
                }
                ul.pagination-arrow li:nth-last-child(2) {
                    margin-left: 1.8125rem;
                }
            }

            @media screen and (max-width: 1024px) {
                width: 100%;
                flex-basis: 100%;
                flex-wrap: wrap;
                position: unset;
                justify-content: flex-start;

                > span {
                    > div {
                        flex-direction: column;
                    }
                    label {
                        text-align: left;
                        line-height: 1.9375rem;
                        padding: 0 0.9375rem 0 0;

                        + div {
                            min-width: 9.3125rem;
                        }
                    }
                }
                nav {
                    ul.pagination-arrow li:nth-child(1) {
                        margin-left: 0;
                    }
                }
            }

            
            
        }
        @media screen and (max-width: 1024px) {            
            align-items: start;
            flex-direction: column;
        }

        `,
        
    },
    totalShippingAddresses: {
        css:css`
        display: flex;
        color: ${themeColors.colorMidGrey};
        font-family: Lato;
        font-size: 0.875rem;
        font-style: italic;
        letter-spacing: 0;
        line-height: 1.9375rem;

        span {
            padding-right: 0.3125rem;
        }

        `,        
    }
};

const styles = addressBookPaginationStyles;

const AddressBookPagination = ({ shipTosDataView, updateSearchFields }: Props) => {
    if (!shipTosDataView.value || !shipTosDataView.pagination) {
        return null;
    }

    const { totalItemCount, page, pageSize, pageSizeOptions } = shipTosDataView.pagination;
    if (totalItemCount === 0) {
        return null;
    }

    const changePage = (newPageIndex: number) => {
        updateSearchFields({
            page: newPageIndex,
        });
    };

    const changeResultsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newPageSize = parseInt(event.currentTarget.value, 10);
        updateSearchFields({
            page: 1,
            pageSize: newPageSize,
        });
    };

    return (
        <>
            <Typography as="div" {...styles.wrapper}>
                <StyledWrapper {...styles.totalShippingAddresses}>
                    <Typography>{translate("You have")}</Typography>
                    <Typography>{totalItemCount}</Typography>
                    <Typography>{translate("Shipping addresses")}</Typography>
                </StyledWrapper>
                <Pagination className="address-pagination"
                    {...styles.pagination}
                    resultsCount={totalItemCount}
                    currentPage={page}
                    resultsPerPage={pageSize}
                    resultsPerPageOptions={pageSizeOptions}
                    onChangePage={changePage}
                    onChangeResultsPerPage={changeResultsPerPage}
                />
            </Typography>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(AddressBookPagination),
    definition: {
        group: "Addresses",
        icon: "house",
        allowedContexts: ["AddressesPage"],
    },
};

export default widgetModule;
